import {
    GetFormalOffersResponse,
    fetchGetApplication,
    fetchGetFormalOffers,
    fetchGetFundingRequirementsIwocapay,
    fetchGetIwocapayBuyerSpendingLimitRequest,
    fetchGetIwocapayPaymentCaptured,
    fetchGetIwocapayPaymentLater,
    fetchGetProductOffers,
    fetchGetStateByStateKey
} from '@iwoca/lapi-client/edge';
import { useStateKey } from '../../iwQueryClient/useStateKey';
import { useQuery } from '@tanstack/react-query';
import { SITE_CONTEXT_CACHE_KEY } from '../../iwQueryClient/iwQueryClient';
import { mapOfferDurationToMonths } from '../../myAccount/iwocaPay/dashboard/mapOfferDurationToMonths';
import { getAccountsQueryConfig } from './queryConfigs';
import { fetchGetBuyerSpendingLimit } from '@iwoca/lapi-client/iwocapay';

interface QueryOptions {
    onSuccess?: (data: any) => void;
    onError?: (error: any) => void;
    refetchInterval?: false | number | ((data: any) => number | false);
    enabled?: boolean;
}

/**
 * Allows to ensure some values of a typed object are not nullable
 *
 * type Item = {
 *   cost: number | null
 *   name: string
 *   date: string | null
 *   rating: number | null
 * }
 *
 * type FullItem = Ensure<Item, 'rating' | 'date'>
 *
 */
type Ensure<T, K extends keyof T> = T & RequiredNotNull<Pick<T, K>>;

type RequiredNotNull<T> = {
    [P in keyof T]-?: NonNullable<T[P]>;
};

type FormalOffers = NonNullable<
    GetFormalOffersResponse['data']
>['formal_offers'];

export type SanitisedFormalOffers = Ensure<
    FormalOffers[number],
    'max_amount' | 'available_cash' | 'timeout'
>[];

const sortFormalOffersByDuration = (
    offers:
        | NonNullable<GetFormalOffersResponse['data']>['formal_offers']
        | undefined
) => {
    if (!offers) return [];

    return offers.sort((a, b) => {
        const aMonths = mapOfferDurationToMonths(a);
        const bMonths = mapOfferDurationToMonths(b);

        return aMonths - bMonths;
    });
};

export function useGetFormalOffers({
    enabled = true
}: { enabled?: boolean } = {}) {
    const stateKey = useStateKey();

    const isEnabled = Boolean(stateKey) && enabled;

    const { data, isLoading, refetch } = useQuery({
        queryKey: ['fetchGetFormalOffers', stateKey!],
        queryFn: () => fetchGetFormalOffers({ stateKey: stateKey! }),
        enabled: isEnabled
    });

    return {
        formalOffers:
            sortFormalOffersByDuration(data?.data?.formal_offers) || null,
        loadingFormalOffers: isLoading && isEnabled,
        fetchFormalOffers: refetch
    };
}

export function useGetFormalOffer(offerId: string | undefined) {
    const stateKey = useStateKey();

    const enabled = Boolean(stateKey) && Boolean(offerId);

    const { data, isLoading } = useQuery({
        queryKey: ['fetchGetFormalOffers', stateKey!],
        queryFn: () => fetchGetFormalOffers({ stateKey: stateKey! }),
        enabled: enabled,
        select: (data) =>
            data.data?.formal_offers.find(
                (formal_offer) => formal_offer.offer_id === offerId
            )
    });

    return {
        formalOffer: data || null,
        loadingFormalOffer: isLoading && Boolean(enabled)
    };
}

type ProductType = NonNullable<
    GetFormalOffersResponse['data']
>['formal_offers'][0]['product_type'][];

export const useApprovedNotTakenOffers = ({
    excludedProducts = []
}: {
    excludedProducts?: ProductType;
} = {}): {
    approvedNotTakenOffers: SanitisedFormalOffers;
    loadingApprovedNotTaken: boolean;
} => {
    const { formalOffers, loadingFormalOffers } = useGetFormalOffers();

    const sanitisedOffers = formalOffers?.filter((offer) => {
        if (!offer.max_amount) return false;
        if (!offer.available_cash) return false;
        if (!offer.timeout) return false;
        return true;
    });

    const validOffers = sanitisedOffers?.filter(
        (offer) => offer.is_valid === true
    );

    const filteredOffers = validOffers?.filter(
        (offer) => !excludedProducts.includes(offer.product_type)
    );

    const notTakenOffers = filteredOffers?.filter((offer) => {
        return Math.abs(offer.max_amount! - offer.available_cash!) < 1;
    });

    return {
        approvedNotTakenOffers: notTakenOffers as SanitisedFormalOffers,
        loadingApprovedNotTaken: loadingFormalOffers
    };
};

export function useGetSpendingLimitRequest() {
    const stateKey = useStateKey();
    const { data, isLoading, error } = useQuery({
        queryKey: ['fetchGetIwocapayBuyerSpendingLimitRequest', stateKey],
        queryFn: () =>
            fetchGetIwocapayBuyerSpendingLimitRequest({ stateKey: stateKey! }),
        enabled: Boolean(stateKey)
    });

    return {
        spendingLimitRequest: data?.data,
        loadingSpendingLimitRequest: isLoading,
        spendingLimitRequestError: error
    };
}

export function useGetFundingRequirementsIwocaPay() {
    const stateKey = useStateKey();
    const { formalOffers } = useGetFormalOffers();

    const { data, isLoading, refetch } = useQuery({
        // eslint-disable-next-line @tanstack/query/exhaustive-deps
        queryKey: ['fetchGetFundingRequirements', stateKey, formalOffers],
        queryFn: () =>
            fetchGetFundingRequirementsIwocapay({
                stateKey: stateKey!,
                offerId: formalOffers[0].offer_id!
            }),
        enabled:
            Boolean(stateKey) &&
            Boolean(formalOffers) &&
            formalOffers.length > 0
    });

    return {
        fundingRequirement: data?.data || null,
        loadingFundingRequirement: isLoading,
        fetchFundingRequirements: refetch
    };
}

// Please keep this list up to date with currently used split tests in Pandora.
// Add any new split tests as required.
type TSplitTestNames =
    | 'skip_completed_drawdown_step_test'
    | 'add_msh_drawdown_modal'
    | 'new_loan_ended_view'
    | 'new_docs_upload'
    | 'uni_approved_view_test_2023'
    | 'new_approval_ui_test_2024'
    | 'homescreen_banner'
    | 'doc_upload_banner';

type splitTests = {
    data: {
        user_split_tests: { [k: string]: boolean }[];
    };
};

export function useSplitTests(splitTestName: TSplitTestNames): boolean {
    const { data: splitTests } = useQuery({
        queryKey: [SITE_CONTEXT_CACHE_KEY],
        select: (data: splitTests) => data.data.user_split_tests
    });

    const splitTestsAsObj = splitTests!.reduce((acc, obj) => {
        const [key, val] = Object.entries(obj)[0];

        return { ...acc, [key]: val };
    }, {});

    return splitTestsAsObj[splitTestName];
}

export function useGetProductOffers(stateKey: string) {
    const { data, isLoading, isError, refetch, isFetching } = useQuery({
        queryKey: ['product_offers', stateKey],
        queryFn: () => fetchGetProductOffers({ stateKey }),
        select: (data) => data.data
    });

    return { data, isLoading, isError, refetch, isFetching };
}

export function useGetApplication(stateKey: string) {
    const { data, isLoading, isError } = useQuery({
        queryKey: ['application', stateKey],
        queryFn: () => fetchGetApplication({ stateKey }),
        select: (data) => data.data
    });

    return { data, isLoading, isError };
}

export function useGetCustomerState(stateKey: string) {
    const { data, isLoading, isError } = useQuery({
        queryKey: ['customer_state', stateKey],
        queryFn: () => fetchGetStateByStateKey({ stateKey }),
        select: (data) => data.data
    });

    return { data, isLoading, isError };
}

export const useGetDocuments = (stateKey: string) => {
    const { data, isLoading, isError } = useQuery({
        queryKey: ['documents', stateKey],
        queryFn: async () => {
            // eslint-disable-next-line pandora/prefer-lapi-client
            const response = await fetch(
                `/api/lending/edge/document_upload/${stateKey}/`
            );
            const data = response.json();
            return data;
        }
    });
    return { data, isLoading, isError };
};

export function useGetAccounts(queryOptions?: QueryOptions) {
    const stateKey = useStateKey();

    return useQuery({
        ...getAccountsQueryConfig(stateKey),
        enabled: Boolean(stateKey) && (queryOptions?.enabled ?? true),
        select: (data) => data.data!
    });
}

export function useGetIwocapayPaymentLater() {
    const stateKey = useStateKey();

    const { data, isLoading } = useQuery({
        queryKey: ['fetchGetIwocapayPaymentLater', stateKey],
        queryFn: () => fetchGetIwocapayPaymentLater({ stateKey })
    });

    return {
        buyerPayLinks: data?.data,
        loadingBuyerPayLinks: isLoading
    };
}

export function useGetIwocapayCapturedPayments() {
    const stateKey = useStateKey();

    const { data, isLoading } = useQuery({
        queryKey: ['fetchGetIwocapayPaymentCaptured', stateKey],
        queryFn: () => fetchGetIwocapayPaymentCaptured({ stateKey })
    });

    return {
        capturedPayments: data?.data,
        loadingCapturedPayments: isLoading
    };
}

export function useGetBuyerSpendingLimit() {
    const stateKey = useStateKey();

    const { data, isLoading } = useQuery({
        queryKey: ['fetchGetBuyerSpendingLimit', stateKey],
        queryFn: () => fetchGetBuyerSpendingLimit({ stateKey })
    });

    return {
        buyerSpendingLimit: data?.data,
        loadingBuyerSpendingLimit: isLoading
    };
}
